import type {AuditBuildCompanyFSTreeItems} from '@/services/auditService/auditCompanyFSCategoryService';
import {hasChildren} from '@/utils/fsDerived';
import {FS_CODE, findNodeByCodes, isCode} from '@/utils/fsCodes';
import {parseAmountDisplay} from '@/utils/dataUtils';

export type LeafValueGetter = (node: AuditBuildCompanyFSTreeItems) => number;
export type ColumnKind = 'input' | 'client' | 'audited';

// ---------- Generic helpers ----------
export function sumDescendants(node: AuditBuildCompanyFSTreeItems, getLeaf: LeafValueGetter): number {
    if (!hasChildren(node)) return getLeaf(node);
    let s = 0;
    for (const ch of (node.children || [])) s += sumDescendants(ch as any, getLeaf);
    return s;
}

/** Synthetic fsId range for company custom PL Level-3 nodes (backend BASE = 2e9). */
const PL_CUSTOM_FS_ID_BASE = 2_000_000_000;

/** True when node is a company custom Level-3 leaf under PL-COT / PL-OI / PL-AE. */
export function isPlCustomFsNode(node: AuditBuildCompanyFSTreeItems | null | undefined): boolean {
    if (!node) return false;
    const any = node as any;
    if (any.isCustom === true || any.customNodeId != null) return true;
    const fsId = Number(any.fsId);
    return Number.isFinite(fsId) && fsId >= PL_CUSTOM_FS_ID_BASE;
}

/**
 * PL-COT amount for formula / subtotal / balancing.
 * Sums children except OI/PUR/CI (inventory formula sides) — includes COS+ROY+SUB+OCT
 * and company custom Level-3 leaves so GP→PAT→BS balancing stay aligned.
 * Falls back to full descendant sum when no includable children exist.
 */
export function sumPlCotFormulaAmount(
    costTurnover: AuditBuildCompanyFSTreeItems | null | undefined,
    getLeaf: LeafValueGetter,
): number {
    if (!costTurnover) return 0;
    const excluded = [
        ...FS_CODE.PL.OPENING_INVENTORIES,
        ...FS_CODE.PL.PURCHASES,
        ...FS_CODE.PL.CLOSING_INVENTORIES,
    ];
    let selective = 0;
    let hasIncludable = false;
    for (const ch of (costTurnover.children || []) as AuditBuildCompanyFSTreeItems[]) {
        if (isCode(ch, ...excluded)) continue;
        hasIncludable = true;
        selective += sumDescendants(ch, getLeaf);
    }
    if (hasIncludable) return selective;
    return sumDescendants(costTurnover, getLeaf);
}

// ---------- BS: Reusable leaf getters and resolvers ----------
export function bsMakeLeafGetter(
    kind: ColumnKind,
    opts: {
        amountDisplays?: Record<number, string>;
        afterTaxFromPL?: number | null;
        afterTaxClientFromPL?: number | null;
        headerCodes?: string[];
        derivedRetainedEarnings?: number | null;
    } = {}
): LeafValueGetter {
    const headerCodes = opts.headerCodes || [];
    const afterTaxAudited = Number(opts.afterTaxFromPL ?? 0) || 0;
    const afterTaxClient = Number(opts.afterTaxClientFromPL ?? 0) || 0;
    const displays = opts.amountDisplays || {};
    const derivedRetainedEarnings = opts.derivedRetainedEarnings;

    return (node: AuditBuildCompanyFSTreeItems): number => {
        if (isCode(node, ...headerCodes)) return 0;
        if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
            if (kind === 'client') return afterTaxClient;
            return afterTaxAudited; // input and audited both reflect audited PL in FS formulas
        }
        if (derivedRetainedEarnings !== undefined && derivedRetainedEarnings !== null && isCode(node, ...FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES)) {
            return derivedRetainedEarnings;
        }
        if (kind === 'input') return parseAmountDisplay(displays[Number((node as any).fsId)] || '');
        if (kind === 'client') return Number((node as any)?.openingAmount ?? (node as any)?.amount ?? 0) || 0;
        return Number((node as any)?.finalAmount ?? 0) || 0; // audited
    };
}

/** BS-EQ-AL stored opening amount from tree map or node fields */
export function getRetainedProfitsStoredAmount(
    bsTree: AuditBuildCompanyFSTreeItems[],
    bsValuesMap?: Record<number, string>,
): number {
    const AL = findNodeByCodes(bsTree, FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES);
    if (!AL) return 0;
    const fsId = Number(AL.fsId);
    if (bsValuesMap && bsValuesMap[fsId] != null) {
        return parseAmountDisplay(bsValuesMap[fsId]);
    }
    return Number((AL as any)?.openingAmount ?? (AL as any)?.amount ?? 0) || 0;
}

export function plAfterTaxLastYearFromMap(
    plTree: AuditBuildCompanyFSTreeItems[] | undefined | null,
    plLastYearMap: Record<number, string>,
): number {
    const afterTax = findNodeByCodes(plTree || [], FS_CODE.PL.AFTER_TAX);
    if (!afterTax) return 0;
    return parseAmountDisplay(plLastYearMap[Number(afterTax.fsId)] ?? '0');
}

/** Last Year AL + Last Year LFP + Last Year DIV */
export function computeDerivedRetainedEarnings(
    bsTree: AuditBuildCompanyFSTreeItems[],
    opts: { bsLastYearMap: Record<number, string>; plAfterTaxLastYear: number },
): number {
    const AL = findNodeByCodes(bsTree, FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES);
    const DIV = findNodeByCodes(bsTree, FS_CODE.BS.DIVIDEND);

    const lastYearGetter: LeafValueGetter = (node) => {
        if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
            return opts.plAfterTaxLastYear;
        }
        return parseAmountDisplay(opts.bsLastYearMap[Number(node.fsId)] ?? '0');
    };

    const val_AL_LY = AL ? sumDescendants(AL as any, lastYearGetter) : 0;
    const val_LFP_LY = opts.plAfterTaxLastYear ?? 0;
    const val_DIV_LY = DIV ? sumDescendants(DIV as any, lastYearGetter) : 0;
    return val_AL_LY + val_LFP_LY + val_DIV_LY;
}

/** Last Year AL = Current AL − Last Year LFP − Last Year DIV（roll-forward 反推） */
export function computeDerivedRetainedEarningsLastYear(
    bsTree: AuditBuildCompanyFSTreeItems[],
    opts: {
        currentRetainedEarnings: number;
        plAfterTaxLastYear: number;
        bsLastYearMap: Record<number, string>;
    },
): number {
    const DIV = findNodeByCodes(bsTree, FS_CODE.BS.DIVIDEND);
    const divGetter: LeafValueGetter = (node) =>
        parseAmountDisplay(opts.bsLastYearMap[Number(node.fsId)] ?? '0');
    const val_DIV_LY = DIV ? sumDescendants(DIV as any, divGetter) : 0;
    return opts.currentRetainedEarnings - opts.plAfterTaxLastYear - val_DIV_LY;
}

/** True when a non-zero DB/Excel saved value differs from the system-computed value */
export function resolveRetainedEarningsUseStored(
    storedOpening: number,
    derivedComputed: number,
): boolean {
    if (storedOpening === 0) return false;
    return Math.round(storedOpening * 100) !== Math.round(derivedComputed * 100);
}

/**
 * True when AL roll-forward inputs have comparative last-year amounts.
 * Only AL / LFP (PL after-tax LY) / DIV — not other BS lines (e.g. INV).
 */
export function hasRetainedEarningsComparativeLastYear(opts: {
    plAfterTaxLastYear: number;
    alLastYear: number;
    divLastYear: number;
}): boolean {
    return Math.abs(Number(opts.plAfterTaxLastYear) || 0) >= 0.005
        || Math.abs(Number(opts.alLastYear) || 0) >= 0.005
        || Math.abs(Number(opts.divLastYear) || 0) >= 0.005;
}

/** AL + DIV last-year leaf sums from a BS last-year map (FS / store path). */
export function retainedEarningsLastYearComponentsFromMap(
    bsTree: AuditBuildCompanyFSTreeItems[],
    bsLastYearMap: Record<number, string>,
    plAfterTaxLastYear: number,
): { alLastYear: number; divLastYear: number; plAfterTaxLastYear: number } {
    const AL = findNodeByCodes(bsTree, FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES);
    const DIV = findNodeByCodes(bsTree, FS_CODE.BS.DIVIDEND);
    const lyGetter: LeafValueGetter = (node) => {
        if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
            return plAfterTaxLastYear;
        }
        return parseAmountDisplay(bsLastYearMap[Number(node.fsId)] ?? '0');
    };
    return {
        alLastYear: AL ? sumDescendants(AL as any, lyGetter) : 0,
        divLastYear: DIV ? sumDescendants(DIV as any, lyGetter) : 0,
        plAfterTaxLastYear: Number(plAfterTaxLastYear) || 0,
    };
}

/**
 * Auto-infer Cap Split from stored≠derived.
 * When derived≈0 and no AL/LFP/DIV comparative LY, treat non-zero stored as stale
 * opening — do NOT auto-Split. Explicit Cap Edit flag is OR'd by callers.
 */
export function resolveRetainedEarningsAutoSplit(
    storedOpening: number,
    derivedComputed: number,
    hasComparativeLastYear: boolean,
): boolean {
    const inferred = resolveRetainedEarningsUseStored(storedOpening, derivedComputed);
    if (!inferred) return false;
    if (!hasComparativeLastYear && Math.abs(derivedComputed) < 0.005) return false;
    return true;
}

/** True when actual (e.g. Audited) differs from roll-forward derived value (cent-level) */
export function resolveRetainedEarningsRollForwardMismatch(
    actual: number,
    derivedComputed: number,
): boolean {
    return Math.round(actual * 100) !== Math.round(derivedComputed * 100);
}

export function inferRetainedEarningsUseStored(
    bsTree: AuditBuildCompanyFSTreeItems[] | undefined | null,
    plTree: AuditBuildCompanyFSTreeItems[] | undefined | null,
    bsValuesMap: Record<number, string>,
    bsLastYearMap: Record<number, string>,
    plLastYearMap: Record<number, string>,
): boolean {
    const tree = (bsTree || []) as AuditBuildCompanyFSTreeItems[];
    if (tree.length === 0) return false;
    const plAfterTaxLastYear = plAfterTaxLastYearFromMap(plTree, plLastYearMap);
    const stored = getRetainedProfitsStoredAmount(tree, bsValuesMap);
    const derived = computeDerivedRetainedEarnings(tree, {bsLastYearMap, plAfterTaxLastYear});
    return resolveRetainedEarningsUseStored(stored, derived);
}

export function bsComputedMap(
    fsTree: AuditBuildCompanyFSTreeItems[],
    kind: ColumnKind,
    opts?: Parameters<typeof bsMakeLeafGetter>[1]
): Record<number, number> {
    return bsComputeComputedAmounts(fsTree, bsMakeLeafGetter(kind, opts));
}

export function bsResolveAdjustmentValue(
    item: AuditBuildCompanyFSTreeItems,
    kind: Exclude<ColumnKind, 'input'>,
    computedMap: Record<number, number>,
    ctx: { afterTaxFromPL?: number | null; afterTaxClientFromPL?: number | null }
): number {
    // Derived BS nodes always use computed map
    if (isCode(item, ...FS_CODE.BS.NET_CURRENT)) return Number(computedMap[Number((item as any).fsId)] ?? 0) || 0;
    if (isCode(item, ...FS_CODE.BS.NET_ASSETS)) return Number(computedMap[Number((item as any).fsId)] ?? 0) || 0;
    if (isCode(item, ...FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES)) return Number(computedMap[Number((item as any).fsId)] ?? 0) || 0;
    // PL link row uses bridged after-tax
    if (isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
        return kind === 'client'
            ? (Number(ctx.afterTaxClientFromPL ?? 0) || 0)
            : (Number(ctx.afterTaxFromPL ?? 0) || 0);
    }
    // Parents use computed sum, leaves use raw value
    if (hasChildren(item)) return Number(computedMap[Number((item as any).fsId)] ?? 0) || 0;
    return kind === 'client'
        ? (Number(((item as any)?.openingAmount ?? (item as any)?.amount) ?? 0) || 0)
        : (Number(((item as any)?.finalAmount) ?? 0) || 0);
}

function sumByProp(node: AuditBuildCompanyFSTreeItems, prop: 'amount' | 'finalAmount' | 'openingAmount'): number {
    const getLeaf: LeafValueGetter = (n) => Number((n as any)?.[prop] ?? 0) || 0;
    return sumDescendants(node, getLeaf);
}

// ---------- Balance Sheet ----------
export function bsComputeComputedAmounts(
    fsTree: AuditBuildCompanyFSTreeItems[],
    getLeaf: LeafValueGetter,
): Record<number, number> {
    const computed: Record<number, number> = {};

    // Generic header sums for all parents
    const visit = (nodes: AuditBuildCompanyFSTreeItems[]) => {
        for (const n of nodes || []) {
            if (hasChildren(n)) {
                computed[Number(n.fsId)] = sumDescendants(n, getLeaf);
                if (Array.isArray(n.children) && n.children.length) visit(n.children as any);
            }
        }
    };
    visit(fsTree);

    // Derived nodes
    const CA = findNodeByCodes(fsTree, FS_CODE.BS.CURRENT_ASSETS);
    const CL = findNodeByCodes(fsTree, FS_CODE.BS.CURRENT_LIABILITIES);
    const NCA = findNodeByCodes(fsTree, FS_CODE.BS.NON_CURRENT_ASSETS);
    const NCL = findNodeByCodes(fsTree, FS_CODE.BS.NON_CURRENT_LIABILITIES);
    const NET_CURRENT = findNodeByCodes(fsTree, FS_CODE.BS.NET_CURRENT);
    const NET_ASSETS = findNodeByCodes(fsTree, FS_CODE.BS.NET_ASSETS);

    const netCurrent = (CA ? sumDescendants(CA, getLeaf) : 0) - (CL ? sumDescendants(CL, getLeaf) : 0);
    if (NET_CURRENT) computed[NET_CURRENT.fsId] = netCurrent;
    const netAssets = (NCA ? sumDescendants(NCA, getLeaf) : 0) + netCurrent - (NCL ? sumDescendants(NCL, getLeaf) : 0);
    if (NET_ASSETS) computed[NET_ASSETS.fsId] = netAssets;

    // Ensure RETAINED_PROFITS_OR_LOSSES is in computed map if it's a leaf (treated as derived if getter handles it)
    const AL = findNodeByCodes(fsTree, FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES);
    if (AL && !hasChildren(AL)) {
        computed[Number(AL.fsId)] = getLeaf(AL);
    }

    return computed;
}

export function bsNameFor(
    item: AuditBuildCompanyFSTreeItems,
    computedAmount: number,
    ctx?: {
        leafAmountGetter?: (node: AuditBuildCompanyFSTreeItems) => number;
        afterTaxFromPL?: number | null;
        periodType?: 'year' | 'period' | null;
    }
): string | undefined {
    if (isCode(item, ...FS_CODE.BS.NET_CURRENT)) return computedAmount >= 0 ? 'NET CURRENT ASSETS' : 'NET CURRENT LIABILITIES';
    if (isCode(item, ...FS_CODE.BS.NET_ASSETS)) return computedAmount >= 0 ? 'NET ASSETS' : 'NET LIABILITIES';
    if (isCode(item, ...FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES)) {
        const val = ctx?.leafAmountGetter ? ctx.leafAmountGetter(item) : computedAmount;
        return val >= 0 ? 'Retained profits' : 'Accumulated losses';
    }
    if (isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
        const val = Number(ctx?.afterTaxFromPL ?? 0);
        const signLabel = val >= 0 ? 'Profit' : 'Loss';
        const type = (ctx?.periodType === 'year' ? 'year' : 'period');
        return `${signLabel} for the ${type}`;
    }
    return undefined;
}

export function bsAdjustmentAmountOverride(
    item: AuditBuildCompanyFSTreeItems,
    fsTree: AuditBuildCompanyFSTreeItems[],
    ctx?: { afterTaxClientFromPL?: number | null },
): number | undefined {
    const CA = findNodeByCodes(fsTree, FS_CODE.BS.CURRENT_ASSETS);
    const CL = findNodeByCodes(fsTree, FS_CODE.BS.CURRENT_LIABILITIES);
    const NCA = findNodeByCodes(fsTree, FS_CODE.BS.NON_CURRENT_ASSETS);
    const NCL = findNodeByCodes(fsTree, FS_CODE.BS.NON_CURRENT_LIABILITIES);
    if (isCode(item, ...FS_CODE.BS.NET_CURRENT)) {
        const ca = CA ? sumByProp(CA, 'openingAmount') : 0;
        const cl = CL ? sumByProp(CL, 'openingAmount') : 0;
        return ca - cl;
    }
    if (isCode(item, ...FS_CODE.BS.NET_ASSETS)) {
        const nca = NCA ? sumByProp(NCA, 'openingAmount') : 0;
        const ncl = NCL ? sumByProp(NCL, 'openingAmount') : 0;
        const ca = CA ? sumByProp(CA, 'openingAmount') : 0;
        const cl = CL ? sumByProp(CL, 'openingAmount') : 0;
        const netCurrent = ca - cl;
        return nca + netCurrent - ncl;
    }
    if (isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) return Number(ctx?.afterTaxClientFromPL ?? 0) || 0;
    return undefined;
}

export function bsAdjustmentFinalOverride(
    item: AuditBuildCompanyFSTreeItems,
    fsTree: AuditBuildCompanyFSTreeItems[],
    ctx?: { afterTaxFromPL?: number | null },
): number | undefined {
    const CA = findNodeByCodes(fsTree, FS_CODE.BS.CURRENT_ASSETS);
    const CL = findNodeByCodes(fsTree, FS_CODE.BS.CURRENT_LIABILITIES);
    const NCA = findNodeByCodes(fsTree, FS_CODE.BS.NON_CURRENT_ASSETS);
    const NCL = findNodeByCodes(fsTree, FS_CODE.BS.NON_CURRENT_LIABILITIES);
    if (isCode(item, ...FS_CODE.BS.NET_CURRENT)) {
        const ca = CA ? sumByProp(CA, 'finalAmount') : 0;
        const cl = CL ? sumByProp(CL, 'finalAmount') : 0;
        return ca - cl;
    }
    if (isCode(item, ...FS_CODE.BS.NET_ASSETS)) {
        const nca = NCA ? sumByProp(NCA, 'finalAmount') : 0;
        const ncl = NCL ? sumByProp(NCL, 'finalAmount') : 0;
        const ca = CA ? sumByProp(CA, 'finalAmount') : 0;
        const cl = CL ? sumByProp(CL, 'finalAmount') : 0;
        const netCurrent = ca - cl;
        return nca + netCurrent - ncl;
    }
    if (isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) return Number(ctx?.afterTaxFromPL ?? 0) || 0;
    return undefined;
}

// ---------- Profit & Loss ----------
export function plComputeComputedAmounts(
    fsTree: AuditBuildCompanyFSTreeItems[],
    getLeaf: LeafValueGetter,
): Record<number, number> {
    const computed: Record<number, number> = {};

    const visit = (nodes: AuditBuildCompanyFSTreeItems[]) => {
        for (const n of nodes || []) {
            if (hasChildren(n)) {
                computed[Number(n.fsId)] = sumDescendants(n, getLeaf);
                if (Array.isArray(n.children) && n.children.length) visit(n.children as any);
            }
        }
    };
    visit(fsTree);

    const TURNOVER = findNodeByCodes(fsTree, FS_CODE.PL.TURNOVER);
    const COST_TURNOVER = findNodeByCodes(fsTree, FS_CODE.PL.COST_OF_TURNOVER);
    const OTHER_INCOME = findNodeByCodes(fsTree, FS_CODE.PL.OTHER_INCOME);
    const ADMIN = findNodeByCodes(fsTree, FS_CODE.PL.ADMIN_EXPENSES);
    const FINANCE = findNodeByCodes(fsTree, FS_CODE.PL.FINANCE_COSTS);
    const GROSS = findNodeByCodes(fsTree, FS_CODE.PL.GROSS);
    const GROSS_OTHER = findNodeByCodes(fsTree, FS_CODE.PL.GROSS_AND_OTHER);
    const BEFORE_TAX = findNodeByCodes(fsTree, FS_CODE.PL.BEFORE_TAX);
    const AFTER_TAX = findNodeByCodes(fsTree, FS_CODE.PL.AFTER_TAX);
    const TAX = findNodeByCodes(fsTree, FS_CODE.PL.TAXATION);

    const turnoverSum = TURNOVER ? sumDescendants(TURNOVER, getLeaf) : 0;
    // PL-COT: ignore OI/PUR/CI; include COS+ROY+SUB+OCT + custom Level-3
    const costSum = sumPlCotFormulaAmount(COST_TURNOVER, getLeaf);
    if (COST_TURNOVER) computed[Number(COST_TURNOVER.fsId)] = costSum;
    const grossValue = turnoverSum - costSum;
    if (GROSS) computed[GROSS.fsId] = grossValue;

    const otherIncomeSum = OTHER_INCOME ? sumDescendants(OTHER_INCOME, getLeaf) : 0;
    const grossAndOtherValue = grossValue + otherIncomeSum;
    if (GROSS_OTHER) computed[GROSS_OTHER.fsId] = grossAndOtherValue;

    const adminSum = ADMIN ? sumDescendants(ADMIN, getLeaf) : 0;
    const financeSum = FINANCE ? sumDescendants(FINANCE, getLeaf) : 0;
    const beforeTaxValue = grossAndOtherValue - adminSum - financeSum;
    if (BEFORE_TAX) computed[BEFORE_TAX.fsId] = beforeTaxValue;

    const taxSum = TAX ? sumDescendants(TAX, getLeaf) : 0;
    const afterTaxValue = beforeTaxValue - taxSum;
    if (AFTER_TAX) computed[AFTER_TAX.fsId] = afterTaxValue;

    return computed;
}

/**
 * 在 COST_TURNOVER 等中间值已写入 computed 后，按公式重算 PL-GP / PL-GPOI / PL-PBT / PL-PAT。
 * 用于 Last Year 列在 PL-COT 汇总被后置覆盖后同步更新衍生节点。
 */
export function recomputePlDerivedFormulaNodes(
    fsTree: AuditBuildCompanyFSTreeItems[],
    computed: Record<number, number>,
    getLeaf: LeafValueGetter,
): void {
    const TURNOVER = findNodeByCodes(fsTree, FS_CODE.PL.TURNOVER);
    const COST_TURNOVER = findNodeByCodes(fsTree, FS_CODE.PL.COST_OF_TURNOVER);
    const OTHER_INCOME = findNodeByCodes(fsTree, FS_CODE.PL.OTHER_INCOME);
    const ADMIN = findNodeByCodes(fsTree, FS_CODE.PL.ADMIN_EXPENSES);
    const FINANCE = findNodeByCodes(fsTree, FS_CODE.PL.FINANCE_COSTS);
    const GROSS = findNodeByCodes(fsTree, FS_CODE.PL.GROSS);
    const GROSS_OTHER = findNodeByCodes(fsTree, FS_CODE.PL.GROSS_AND_OTHER);
    const BEFORE_TAX = findNodeByCodes(fsTree, FS_CODE.PL.BEFORE_TAX);
    const AFTER_TAX = findNodeByCodes(fsTree, FS_CODE.PL.AFTER_TAX);
    const TAX = findNodeByCodes(fsTree, FS_CODE.PL.TAXATION);

    const turnoverSum = TURNOVER ? sumDescendants(TURNOVER, getLeaf) : 0;
    const costSum = COST_TURNOVER
        ? (Number(computed[Number(COST_TURNOVER.fsId)] ?? 0) || sumDescendants(COST_TURNOVER, getLeaf))
        : 0;
    const grossValue = turnoverSum - costSum;
    if (GROSS) computed[Number(GROSS.fsId)] = grossValue;

    const otherIncomeSum = OTHER_INCOME ? sumDescendants(OTHER_INCOME, getLeaf) : 0;
    const grossAndOtherValue = grossValue + otherIncomeSum;
    if (GROSS_OTHER) computed[Number(GROSS_OTHER.fsId)] = grossAndOtherValue;

    const adminSum = ADMIN ? sumDescendants(ADMIN, getLeaf) : 0;
    const financeSum = FINANCE ? sumDescendants(FINANCE, getLeaf) : 0;
    const beforeTaxValue = grossAndOtherValue - adminSum - financeSum;
    if (BEFORE_TAX) computed[Number(BEFORE_TAX.fsId)] = beforeTaxValue;

    const taxSum = TAX ? sumDescendants(TAX, getLeaf) : 0;
    const afterTaxValue = beforeTaxValue - taxSum;
    if (AFTER_TAX) computed[Number(AFTER_TAX.fsId)] = afterTaxValue;
}

export function plNameFor(item: AuditBuildCompanyFSTreeItems, computedAmount: number): string | undefined {
    if (isCode(item, ...FS_CODE.PL.GROSS)) return computedAmount >= 0 ? 'GROSS PROFIT' : 'GROSS LOSS';
    if (isCode(item, ...FS_CODE.PL.GROSS_AND_OTHER)) return computedAmount >= 0 ? 'GROSS PROFIT AND OTHER INCOME' : 'GROSS LOSS AND OTHER INCOME';
    if (isCode(item, ...FS_CODE.PL.BEFORE_TAX)) return computedAmount >= 0 ? 'PROFIT BEFORE TAX' : 'LOSS BEFORE TAX';
    if (isCode(item, ...FS_CODE.PL.AFTER_TAX)) return computedAmount >= 0 ? 'PROFIT AFTER TAX' : 'LOSS AFTER TAX';
    return undefined;
}

export function plAdjustmentAmountOverride(item: AuditBuildCompanyFSTreeItems, fsTree: AuditBuildCompanyFSTreeItems[]): number | undefined {
    const TURNOVER = findNodeByCodes(fsTree as any, FS_CODE.PL.TURNOVER);
    const COST_TURNOVER = findNodeByCodes(fsTree as any, FS_CODE.PL.COST_OF_TURNOVER);
    const OTHER_INCOME = findNodeByCodes(fsTree as any, FS_CODE.PL.OTHER_INCOME);
    const ADMIN = findNodeByCodes(fsTree as any, FS_CODE.PL.ADMIN_EXPENSES);
    const FINANCE = findNodeByCodes(fsTree as any, FS_CODE.PL.FINANCE_COSTS);
    const TAX = findNodeByCodes(fsTree as any, FS_CODE.PL.TAXATION);

    const sumOpening = (n: any) => sumByProp(n, 'openingAmount');
    const turnoverSum = TURNOVER ? sumOpening(TURNOVER) : 0;
    const costSum = sumPlCotFormulaAmount(COST_TURNOVER as any, (n) => Number((n as any)?.openingAmount ?? 0) || 0);
    const gross = turnoverSum - costSum;
    if (isCode(item, ...FS_CODE.PL.GROSS)) return gross;
    if (isCode(item, ...FS_CODE.PL.GROSS_AND_OTHER)) return gross + (OTHER_INCOME ? sumOpening(OTHER_INCOME) : 0);
    if (isCode(item, ...FS_CODE.PL.BEFORE_TAX)) {
        const other = OTHER_INCOME ? sumOpening(OTHER_INCOME) : 0;
        const admin = ADMIN ? sumOpening(ADMIN) : 0;
        const fin = FINANCE ? sumOpening(FINANCE) : 0;
        return gross + other - admin - fin;
    }
    if (isCode(item, ...FS_CODE.PL.AFTER_TAX)) {
        const other = OTHER_INCOME ? sumOpening(OTHER_INCOME) : 0;
        const admin = ADMIN ? sumOpening(ADMIN) : 0;
        const fin = FINANCE ? sumOpening(FINANCE) : 0;
        const before = gross + other - admin - fin;
        const tax = TAX ? sumOpening(TAX) : 0;
        return before - tax;
    }
    return undefined;
}

export function plAdjustmentFinalOverride(item: AuditBuildCompanyFSTreeItems, fsTree: AuditBuildCompanyFSTreeItems[]): number | undefined {
    const TURNOVER = findNodeByCodes(fsTree, FS_CODE.PL.TURNOVER);
    const COST_TURNOVER = findNodeByCodes(fsTree, FS_CODE.PL.COST_OF_TURNOVER);
    const OTHER_INCOME = findNodeByCodes(fsTree, FS_CODE.PL.OTHER_INCOME);
    const ADMIN = findNodeByCodes(fsTree, FS_CODE.PL.ADMIN_EXPENSES);
    const FINANCE = findNodeByCodes(fsTree, FS_CODE.PL.FINANCE_COSTS);
    const TAX = findNodeByCodes(fsTree, FS_CODE.PL.TAXATION);

    const sumFinal = (n: any) => sumByProp(n, 'finalAmount');
    const turnoverSum = TURNOVER ? sumFinal(TURNOVER) : 0;
    const costSum = sumPlCotFormulaAmount(COST_TURNOVER as any, (n) => Number((n as any)?.finalAmount ?? 0) || 0);
    const gross = turnoverSum - costSum;
    if (isCode(item, ...FS_CODE.PL.GROSS)) return gross;
    if (isCode(item, ...FS_CODE.PL.GROSS_AND_OTHER)) return gross + (OTHER_INCOME ? sumFinal(OTHER_INCOME) : 0);
    if (isCode(item, ...FS_CODE.PL.BEFORE_TAX)) {
        const other = OTHER_INCOME ? sumFinal(OTHER_INCOME) : 0;
        const admin = ADMIN ? sumFinal(ADMIN) : 0;
        const fin = FINANCE ? sumFinal(FINANCE) : 0;
        return gross + other - admin - fin;
    }
    if (isCode(item, ...FS_CODE.PL.AFTER_TAX)) {
        const other = OTHER_INCOME ? sumFinal(OTHER_INCOME) : 0;
        const admin = ADMIN ? sumFinal(ADMIN) : 0;
        const fin = FINANCE ? sumFinal(FINANCE) : 0;
        const before = gross + other - admin - fin;
        const tax = TAX ? sumFinal(TAX) : 0;
        return before - tax;
    }
    return undefined;
}

export function plIsProtectedParentHeader(item: AuditBuildCompanyFSTreeItems): boolean {
    return (
        isCode(item, ...FS_CODE.PL.TURNOVER) ||
        isCode(item, ...FS_CODE.PL.COST_OF_TURNOVER) ||
        isCode(item, ...FS_CODE.PL.OTHER_INCOME) ||
        isCode(item, ...FS_CODE.PL.ADMIN_EXPENSES) ||
        isCode(item, ...FS_CODE.PL.FINANCE_COSTS) ||
        isCode(item, ...FS_CODE.PL.GROSS) ||
        isCode(item, ...FS_CODE.PL.GROSS_AND_OTHER) ||
        isCode(item, ...FS_CODE.PL.BEFORE_TAX) ||
        isCode(item, ...FS_CODE.PL.AFTER_TAX)
    );
}

// ---------- PL: Reusable leaf getters and resolvers ----------
export function plMakeLeafGetter(
    kind: ColumnKind,
    opts: { amountDisplays?: Record<number, string> } = {}
): LeafValueGetter {
    const displays = opts.amountDisplays || {};
    return (node: AuditBuildCompanyFSTreeItems): number => {
        if (kind === 'input') {
            // In FS edit mode, protected parent headers are derived; treat as zero leaf
            if (plIsProtectedParentHeader(node)) return 0;
            return parseAmountDisplay(displays[Number((node as any).fsId)] || '');
        }
        if (kind === 'client') return Number((node as any)?.openingAmount ?? (node as any)?.amount ?? 0) || 0;
        return Number((node as any)?.finalAmount ?? 0) || 0; // audited
    };
}

export function plComputedMap(
    fsTree: AuditBuildCompanyFSTreeItems[],
    kind: ColumnKind,
    opts?: Parameters<typeof plMakeLeafGetter>[1]
): Record<number, number> {
    return plComputeComputedAmounts(fsTree, plMakeLeafGetter(kind, opts));
}

const PL_COT_AUDITED_OVERRIDE_CODES = new Set([
    'PL-COT-OI', 'PL-COT-PUR', 'PL-COT-CI', 'PL-COT-COS',
]);

/** Sum adjustment[] on a single FS node (leaf). */
export function sumPlLeafAdjustments(node: any): number {
    if (!Array.isArray(node?.adjustment)) return 0;
    return node.adjustment.reduce((sum: number, adj: any) => sum + (Number(adj?.adjustment) || 0), 0);
}

/**
 * adjustment 模式叶子节点 Audited：默认 Client + Σadjustment；
 * PL-COT 四节点使用 leafAuditedValues（含 COS/PUR 输入模式 override）。
 */
/** PL-AE-ADM 等底稿节点：Audited 以接口返回的 audited（finalAmount）为准 */
export function resolvePlAdjustByPaperAuditedValue(node: AuditBuildCompanyFSTreeItems | any): number | null {
    if (!isCode(node, 'PL-AE-ADM') || !(node as any)?.adjustByPaper) return null;
    return Number((node as any)?.finalAmount ?? 0) || 0;
}

export function resolvePlLeafAuditedValue(
    node: AuditBuildCompanyFSTreeItems | any,
    leafClientValues: Record<number, number>,
    leafAuditedValues: Record<number, number>,
): number {
    const adjustByPaperAudited = resolvePlAdjustByPaperAuditedValue(node);
    if (adjustByPaperAudited != null) return adjustByPaperAudited;

    const fsId = Number((node as any)?.fsId);
    const code = String((node as any)?.code || '').trim().toUpperCase();
    if (PL_COT_AUDITED_OVERRIDE_CODES.has(code)) {
        return Number(leafAuditedValues[fsId] ?? 0) || 0;
    }
    const client = Number(
        leafClientValues[fsId] ?? (node as any)?.openingAmount ?? (node as any)?.amount ?? 0,
    ) || 0;
    return client + sumPlLeafAdjustments(node);
}

export function plResolveAdjustmentValue(
    item: AuditBuildCompanyFSTreeItems,
    kind: Exclude<ColumnKind, 'input'>,
    computedMap: Record<number, number>
): number {
    // Derived PL nodes always use computed map
    if (isCode(item, ...FS_CODE.PL.GROSS)) return Number(computedMap[Number((item as any).fsId)] ?? 0) || 0;
    if (isCode(item, ...FS_CODE.PL.GROSS_AND_OTHER)) return Number(computedMap[Number((item as any).fsId)] ?? 0) || 0;
    if (isCode(item, ...FS_CODE.PL.BEFORE_TAX)) return Number(computedMap[Number((item as any).fsId)] ?? 0) || 0;
    if (isCode(item, ...FS_CODE.PL.AFTER_TAX)) return Number(computedMap[Number((item as any).fsId)] ?? 0) || 0;
    // Parents use computed sum, leaves use raw value
    if (hasChildren(item)) return Number(computedMap[Number((item as any).fsId)] ?? 0) || 0;
    return kind === 'client'
        ? (Number(((item as any)?.openingAmount ?? (item as any)?.amount) ?? 0) || 0)
        : (Number(((item as any)?.finalAmount) ?? 0) || 0);
}